import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { LogoMark } from "@/components/dw/Logo";

const CURRENCIES = ["INR", "PKR", "USD"];
const EMPTY = {
  name: "", email: "", phone: "", city: "", qualification: "", experience_years: "",
  languages: "", notice_period: "", joining_date: "", currency: "INR", expected_ctc: "", expected_salary: "",
};

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--text-secondary)" }}>
        {label}{required && <span style={{ color: "var(--red)" }}> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function JoinTeam() {
  const [f, setF] = useState(EMPTY);
  const [resume, setResume] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (resume && resume.size > 5 * 1024 * 1024) return toast.error("Resume must be under 5 MB");
    setBusy(true);
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (resume) fd.append("resume", resume);
    try {
      await api.post("/careers/apply", fd);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header className="border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <LogoMark size={36} />
            <span className="font-display text-xl" style={{ color: "var(--primary)" }}>DietWalaa</span>
          </Link>
          <Link to="/" className="text-xs font-bold flex items-center gap-1" style={{ color: "var(--primary)" }}>
            <ArrowLeft size={12} /> Back to home
          </Link>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-4 pt-10 pb-16">
        <div className="section-eyebrow">Careers at Dietwalaa</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: "var(--primary)" }}>
          Join our dietitian team
        </h1>
        <p className="mt-3 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
          Work with Dt Manthan Sharma and help families in India and abroad eat better. Fill in your details and our team will get back to you.
        </p>

        {done ? <ThankYou /> : (
          <form onSubmit={submit} data-testid="join-team-form" className="card-soft mt-8 p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
            <Field label="Full name" required><Input data-testid="jt-name" required value={f.name} onChange={set("name")} /></Field>
            <Field label="Email" required><Input data-testid="jt-email" type="email" required value={f.email} onChange={set("email")} /></Field>
            <Field label="Phone / WhatsApp" required><Input data-testid="jt-phone" required value={f.phone} onChange={set("phone")} /></Field>
            <Field label="City"><Input data-testid="jt-city" value={f.city} onChange={set("city")} /></Field>
            <Field label="Qualification" required><Input data-testid="jt-qualification" required placeholder="e.g. M.Sc Food & Nutrition" value={f.qualification} onChange={set("qualification")} /></Field>
            <Field label="Years of experience" required><Input data-testid="jt-experience" type="number" min="0" step="0.5" required value={f.experience_years} onChange={set("experience_years")} /></Field>
            <Field label="Languages spoken"><Input data-testid="jt-languages" placeholder="Hindi, English, Punjabi" value={f.languages} onChange={set("languages")} /></Field>
            <Field label="Notice period"><Input data-testid="jt-notice" placeholder="e.g. 30 days / Immediate" value={f.notice_period} onChange={set("notice_period")} /></Field>
            <Field label="Earliest joining date"><Input data-testid="jt-joining" type="date" value={f.joining_date} onChange={set("joining_date")} /></Field>
            <Field label="Currency" required>
              <select data-testid="jt-currency" value={f.currency} onChange={set("currency")} className="w-full h-9 rounded-md border px-3 text-sm bg-transparent" style={{ borderColor: "var(--border-strong)" }}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={`Expected CTC (yearly, ${f.currency})`} required><Input data-testid="jt-ctc" type="number" min="0" required value={f.expected_ctc} onChange={set("expected_ctc")} /></Field>
            <Field label={`Expected salary (monthly, ${f.currency})`} required><Input data-testid="jt-salary" type="number" min="0" required value={f.expected_salary} onChange={set("expected_salary")} /></Field>
            <div className="sm:col-span-2">
              <Field label="Resume / CV (PDF or Word, max 5 MB)">
                <label data-testid="jt-resume-label" className="flex items-center gap-2 cursor-pointer rounded-md border-2 border-dashed px-4 py-3 text-sm font-semibold" style={{ borderColor: "var(--border-strong)", color: "var(--primary)" }}>
                  <Upload size={16} /> {resume ? resume.name : "Choose file"}
                  <input data-testid="jt-resume" type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => setResume(e.target.files?.[0] || null)} />
                </label>
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button data-testid="jt-submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--yellow)" }}>
                {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Submitting..." : "Submit application"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function ThankYou() {
  return (
    <div data-testid="join-team-success" className="card-soft mt-8 p-8 text-center">
      <CheckCircle2 size={40} className="mx-auto" style={{ color: "var(--primary)" }} />
      <h2 className="mt-3 font-display text-2xl" style={{ color: "var(--primary)" }}>Application received</h2>
      <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>Thank you! Our team will review your profile and contact you soon.</p>
    </div>
  );
}
